import { useQuery } from "@tanstack/react-query";
import { socialApi } from "@bw/shared/api";
import type { PlayerCard } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

const SEARCH_SCOPE = "all" as const;

/**
 * General by-name player search for the host-only "Add players" picker (GET
 * /social/players/search?q=&scope=all). A host adds a friend/teammate by NAME —
 * NOT only players who have already booked at this facility — so the search uses
 * the broad `scope="all"` path. (Looking players up by facility was removed for
 * privacy — #1657.) Calls the SHARED social client through a LOCAL booking hook
 * — the social
 * module's own usePlayerSearch cannot be imported across the sibling-module
 * boundary (module isolation: a module reaches another only via @bw/shared or by
 * PATH, never by a direct cross-module import).
 *
 * Returns the server's PlayerCard[] — never a client sort, never a derived
 * relationship/rating. The server already excludes the caller's self and
 * both-way blocks; `excludeUserIds` additionally drops anyone ALREADY on this
 * booking's roster (and is a defensive belt-and-braces self-exclusion) so the
 * host can't pick a dupe — the add would 409. The caller passes the DEBOUNCED,
 * TRIMMED query; the query only RUNS at ≥ 2 chars (`enabled`) — the server
 * requires q (min_length=2) and the client guards a blank query. Keyed by the
 * trimmed query so each distinct search gets its OWN cache slot.
 */
export function usePlayerSearch(query: string, excludeUserIds: string[] = []) {
  const trimmed = query.trim();
  const enabled = trimmed.length >= 2;
  const exclude = new Set(excludeUserIds);
  return useQuery<PlayerCard[]>({
    queryKey: queryKeys.social.playerSearch(trimmed, SEARCH_SCOPE),
    queryFn: () => socialApi.searchPlayers(trimmed, SEARCH_SCOPE),
    enabled,
    // The server owns the search; we only DROP rows the host can't validly add
    // (already on the roster / self). No sort, no other transform.
    select: (rows) => rows.filter((p) => !exclude.has(p.user_id)),
  });
}
